import { cleanStringToBeUrlReadable } from 'src/default/helpers/clean-string'
import { ALL_ORGANIZATION_NAMES } from 'src/v2/commons/config'

import { ALL_ROUTES } from '../constants/route-configs.constants'
import { RouteKey, RouteParams } from '../types/routing.types'

export const hasOrganizationPrefix = (path: string): boolean => {
  if (!path || path === '/') return false

  const normalizedPath = path.startsWith('/') ? path.substring(1) : path
  const segments = normalizedPath.split('/')

  const organizationNameSegment = segments[0]

  return ALL_ORGANIZATION_NAMES.some(
    (orgName) =>
      cleanStringToBeUrlReadable(orgName) === organizationNameSegment,
  )
}

export const buildRoute = (
  routeKey: RouteKey,
  params?: RouteParams[RouteKey],
  organizationName?: string,
  includeOrgPrefix: boolean = true,
): string => {
  if (routeKey === 'CUSTOM_PATH' && params && 'path' in params) {
    const customPath = params.path as string

    if (
      hasOrganizationPrefix(customPath) ||
      !includeOrgPrefix ||
      !organizationName
    ) {
      return customPath.startsWith('/') ? customPath : `/${customPath}`
    }

    return `/${cleanStringToBeUrlReadable(organizationName)}${
      customPath.startsWith('/') ? customPath : `/${customPath}`
    }`
  }

  const routeConfig = ALL_ROUTES[routeKey]
  if (!routeConfig) {
    return '/'
  }

  let path = routeConfig.path

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      path = path.replace(`:${key}`, value)
    })
  }

  if (includeOrgPrefix && organizationName && !hasOrganizationPrefix(path)) {
    return `/${cleanStringToBeUrlReadable(organizationName)}${
      path.startsWith('/') ? path : `/${path}`
    }`
  }

  return path.startsWith('/') ? path : `/${path}`
}

export const getRequiredPolicy = (routeKey: RouteKey): string | null => {
  const route = ALL_ROUTES[routeKey]
  return route?.policy || null
}

export const hasRoutePermission = (
  routeKey: RouteKey,
  userPolicies: string[],
): boolean => {
  const policy = getRequiredPolicy(routeKey)

  if (policy === null) {
    return true
  }

  return userPolicies.includes(policy)
}

export const findRouteKeyByPath = (path: string): RouteKey | null => {
  const normalizedPath = normalizePath(path)

  for (const [key, config] of Object.entries(ALL_ROUTES)) {
    const routePath = normalizePath(config.path)

    if (routePath === normalizedPath) {
      return key as RouteKey
    }

    if (
      routePath.includes(':') &&
      pathsMatchWithParams(routePath, normalizedPath)
    ) {
      return key as RouteKey
    }
  }

  return null
}

export const normalizePath = (path: string): string => {
  return path.split('?')[0].split('#')[0]
}

export const pathsMatchWithParams = (
  routePattern: string,
  actualPath: string,
): boolean => {
  const routeParts = routePattern.split('/').filter(Boolean)
  const pathParts = actualPath.split('/').filter(Boolean)

  if (routeParts.length !== pathParts.length) {
    return false
  }

  for (let i = 0; i < routeParts.length; i++) {
    if (routeParts[i].startsWith(':')) {
      continue
    }

    if (routeParts[i] !== pathParts[i]) {
      return false
    }
  }

  return true
}

export const extractParams = (
  routePattern: string,
  actualPath: string,
): Record<string, string> => {
  const params: Record<string, string> = {}
  const routeParts = routePattern.split('/').filter(Boolean)
  const pathParts = actualPath.split('/').filter(Boolean)

  if (routeParts.length !== pathParts.length) {
    return params
  }

  for (let i = 0; i < routeParts.length; i++) {
    if (routeParts[i].startsWith(':')) {
      const paramName = routeParts[i].substring(1)
      params[paramName] = pathParts[i]
    }
  }

  return params
}

export const getRoutePath = (route: string) => {
  return ALL_ROUTES[route]?.path
}
